import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import "./App.css";
import Navbar from "./components/Navbar/Navbar";
import MessageBanner from "./components/MessageBanner";
import Home from "./components/Home/Home";
import Login from "./components/Login/Login";
import FixedRoute from "./components/FixedRoute/FixedRoute";
import SpecialServices from "./components/SpecialServices/SpecialServices";
import ErrorPage from "./components/ErrorPage.js";
import Unauthorized from "./components/Unauthorized";
import SignedOutSplash from "./components/SignedOutSplash";
import AccessLevelRoute from "./utils/AccessLevelRoute";
import PrivateRoutes from "./utils/PrivateRoutes";
import { AuthProvider } from "./utils/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { SESSION_EXPIRED_EVENT } from "./utils/apiClient";
import { clearLocalSession, redirectToLogin } from "./utils/handleLogout";

const App = () => {
  const [apiErrors, setApiErrors] = useState([]);
  const [signedOut, setSignedOut] = useState(false);

  // Listener lives at the App root so the splash survives any sibling
  // unmount. See changes/decisions/2026-05-14_signed-out-splash.md.
  useEffect(() => {
    const onSessionExpired = () => {
      clearLocalSession();
      setApiErrors([]);
      setSignedOut(true);
    };
    globalThis.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () =>
      globalThis.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  if (signedOut) {
    return <SignedOutSplash onSignIn={redirectToLogin} />;
  }

  // Defense in depth: keep 401s from ever preempting the splash via ErrorPage.
  const nonAuthErrors = apiErrors.filter(
    (err) => err?.code !== 401 && err?.status !== 401,
  );

  if (nonAuthErrors.length > 0) {
    return (
      <ErrorPage
        errors={nonAuthErrors}
        homeUrl="/"
        setApiErrors={setApiErrors}
      />
    );
  }

  return (
    <ErrorBoundary>
      <Router basename={process.env.REACT_APP_ROUTER_BASE || ""}>
        <AuthProvider>
          <Navbar />
          <MessageBanner />
          <Routes>
            <Route element={<PrivateRoutes />}>
              <Route path="/" element={<Home />} />
              <Route
                path="/FixedRoute"
                element={
                  <AccessLevelRoute
                    path="/FixedRoute"
                    element={<FixedRoute setApiErrors={setApiErrors} />}
                  />
                }
              />
              <Route
                path="/SpecialServices"
                element={
                  <AccessLevelRoute
                    path="/SpecialServices"
                    element={<SpecialServices setApiErrors={setApiErrors} />}
                  />
                }
              />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
          </Routes>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
